import React from 'react';
import SectionCard from '../components/SectionCard';
import { calculImpactBusiness, formatEuros } from '../utils/roi';
import { PLAN_BY_ID, resolvePlanId } from '../../constants/plans';
import { formatPrice } from '../../utils/format';

/**
 * Impact business — chiffres et mapping backend (version mobile).
 *
 * Backend (routes/dashboard.js)  →  Mobile (display)
 *   stats.roi_estime              →  "ROI estimé ce mois" (formule: satisfaits × ticket × 0.3)
 *   stats.satisfaits              →  "Avis collectés ce mois"
 *   stats.feedbacks_negatifs × ticket → "Perte évitée (estimation)"
 *   user.plan + constants/plans.js → sous-titre dynamique "×N votre abonnement (X €)"
 *
 * Sous-titre :
 *   - plan actif + roi > 0 → "×N votre abonnement (X €)" (1 décimale)
 *   - trial / pas de plan  → "soit Y € de valeur générée ce mois"
 *   - roi = 0              → "Vos premiers ROI apparaîtront avec vos premiers avis collectés"
 *
 * Aligné avec le composant desktop ImpactBusiness — voir DocBlock du composant.
 */
export default function ImpactBusinessSection({ stats, restaurant, user }) {
  const impact = calculImpactBusiness(stats, restaurant?.ticket_moyen);

  // Sprint 2 P1 ROI honnête — prix dynamique depuis constants/plans.js (plus de "89€" hardcodé).
  const subscriptionPrice = PLAN_BY_ID[resolvePlanId(user?.plan)]?.price ?? null;
  const roiMultiple = (subscriptionPrice && subscriptionPrice > 0 && impact.roi_total > 0)
    ? (impact.roi_total / subscriptionPrice).toFixed(1)
    : null;

  let roiSubtitle;
  if (impact.roi_total <= 0) {
    roiSubtitle = 'Vos premiers ROI apparaîtront avec vos premiers avis collectés';
  } else if (subscriptionPrice && roiMultiple) {
    // formatPrice gère les décimales (9,99 € et non 9.99 €). Vue mobile FR.
    roiSubtitle = `×${roiMultiple} votre abonnement (${formatPrice(subscriptionPrice, 'fr')})`;
  } else {
    roiSubtitle = `soit ${formatEuros(impact.roi_total)} de valeur générée ce mois`;
  }

  return (
    <SectionCard title="💰 Impact business estimé">
      <ul style={styles.list}>
        <li style={styles.row}>
          <span style={styles.label}>Avis collectés ce mois</span>
          <span style={{ ...styles.value, color: impact.couverts_supp > 0 ? '#2D7A4A' : '#1A1A1A' }}>
            {stats?.satisfaits > 0 ? `+${stats.satisfaits}` : (stats?.satisfaits ?? 0)} avis
          </span>
        </li>
        <li style={styles.row}>
          <span style={styles.label}>Valeur générée (estimation)</span>
          <span style={{ ...styles.value, color: impact.ca_genere > 0 ? '#2D7A4A' : '#1A1A1A' }}>
            {formatEuros(impact.ca_genere, true)}
          </span>
        </li>
        <li style={styles.row}>
          <span style={styles.label}>Perte évitée (estimation)</span>
          <span style={{ ...styles.value, color: '#E8502A' }}>
            ~{formatEuros(impact.perte_evitee)}
          </span>
        </li>
      </ul>

      <div style={styles.roiBox}>
        <div style={styles.roiTitle}>ROI estimé ce mois</div>
        <div style={styles.roiAmount}>{formatEuros(impact.roi_total, true)}</div>
        <div style={styles.roiHint}>{roiSubtitle}</div>
      </div>
    </SectionCard>
  );
}

const styles = {
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 14,
  },
  label: {
    color: '#6B6B6B',
  },
  value: {
    fontWeight: 700,
    color: '#1A1A1A',
  },
  roiBox: {
    background: '#E8F5EE',
    borderRadius: 12,
    padding: 14,
    marginTop: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  roiTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#2D7A4A',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  roiAmount: {
    fontSize: 28,
    fontWeight: 700,
    color: '#2D7A4A',
    letterSpacing: '-0.02em',
  },
  roiHint: {
    fontSize: 12,
    color: '#6B6B6B',
  },
};
